/**
 * 카카오 주소 검색 모달 (DaumPostcodeEmbed 기반)
 */
import { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DaumPostcodeEmbed from 'react-daum-postcode';
import type { Address } from 'react-daum-postcode';

export interface AddressSearchResult {
    zonecode: string;
    address: string;
    detail: string;
}

interface AddressSearchModalProps {
    open: boolean;
    onClose: () => void;
    onSelect: (data: AddressSearchResult) => void;
}

const AddressSearchModal = ({ open, onClose, onSelect }: AddressSearchModalProps) => {
    const [picked, setPicked] = useState<{ zonecode: string; address: string } | null>(null);
    const [detail, setDetail] = useState('');

    const handleDaumComplete = useCallback((data: Address) => {
        setPicked({ zonecode: data.zonecode, address: data.address });
        setDetail('');
    }, []);

    const handleConfirm = useCallback(() => {
        if (!picked) return;
        onSelect({ zonecode: picked.zonecode, address: picked.address, detail: detail.trim() });
        setPicked(null);
        setDetail('');
        onClose();
    }, [picked, detail, onSelect, onClose]);

    const handleClose = useCallback(() => {
        setPicked(null);
        setDetail('');
        onClose();
    }, [onClose]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    animation: 'fadeIn 0.25s ease-out',
                    '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateY(16px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #E2E0DC',
                    py: 1.5,
                    px: 2.5,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 20, color: '#2563EB !important' }} />
                    <Typography variant="body1" sx={{ fontWeight: 700, fontSize: 16 }}>
                        주소 검색
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.1)' } }}
                >
                    <CloseIcon sx={{ color: '#9B9B9B !important' }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ height: 500, display: picked ? 'none' : 'block' }}>
                    <DaumPostcodeEmbed
                        onComplete={handleDaumComplete}
                        style={{ width: '100%', height: '100%' }}
                    />
                </Box>
                {picked && (
                    <Box
                        sx={{
                            p: 3,
                            animation: 'fadeIn 0.2s ease-out',
                            '@keyframes fadeIn': {
                                from: { opacity: 0, transform: 'translateY(8px)' },
                                to: { opacity: 1, transform: 'translateY(0)' },
                            },
                        }}
                    >
                        {/* Selected Address */}
                        <Box sx={{ bgcolor: '#EFF6FF', borderRadius: 2.5, p: 2, mb: 2 }}>
                            <Typography variant="caption" sx={{ color: '#2563EB !important', fontWeight: 600, display: 'block', mb: 0.5, fontSize: 13 }}>
                                선택된 주소
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14 }}>
                                ({picked.zonecode}) {picked.address}
                            </Typography>
                        </Box>

                        {/* Detail Address Input */}
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#6B6B6B !important', mb: 0.75, fontSize: 14 }}>
                            상세주소 입력
                        </Typography>
                        <TextField
                            fullWidth
                            autoFocus
                            placeholder="동/호수 등 상세주소 입력"
                            value={detail}
                            onChange={(e) => setDetail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); }}
                            sx={{ mb: 2, '& .MuiInputBase-root': { height: 44, borderRadius: '10px', fontSize: 15 } }}
                        />

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<ArrowBackIcon />}
                                onClick={() => setPicked(null)}
                                sx={{ cursor: 'pointer', transition: 'all 0.2s ease', borderRadius: '10px', height: 44, fontSize: 13, fontWeight: 600 }}
                            >
                                <Typography variant="body2" sx={{ fontSize: 13 }}>뒤로</Typography>
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleConfirm}
                                sx={{ cursor: 'pointer', transition: 'all 0.2s ease', borderRadius: '10px', height: 48, fontSize: 15, fontWeight: 600 }}
                            >
                                <Typography variant="body2" sx={{ color: '#fff !important', fontWeight: 600, fontSize: 15 }}>
                                    주소 확정
                                </Typography>
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AddressSearchModal;
